import React, { useState } from "react";
import { useRefinementList } from "react-instantsearch";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FacetModal from "./FacetModal";

// Facette standard, masquée si aucun résultat
export function OptionalRefinementList({ attribute, title, searchable, searchablePlaceholder, limit = 10, showMoreLimit = 100, labelMap, extractLabel, formatLabel }) {
  const { items, refine, canToggleShowMore, isShowingMore, toggleShowMore } = useRefinementList({ attribute, limit, showMoreLimit });
  const [searchQuery, setSearchQuery] = useState("");

  if (!items || items.length === 0) return null;

  const resolveLabel = (label, count) => {
    if (formatLabel) return formatLabel(label, count);
    if (extractLabel) return extractLabel(label);
    if (labelMap && labelMap[label]) return labelMap[label];
    return label;
  };

  const filteredItems = searchQuery.trim() === ""
    ? items
    : items.filter(item => resolveLabel(item.label).toLowerCase().includes(searchQuery.toLowerCase()));

  return (
    <div className="sui-facet">
      <legend className="sui-facet__title">{title}</legend>
      {searchable && (
        <div className="sui-facet-search" style={{ marginBottom: "8px" }}>
          <input
            className="sui-facet-search__text-input"
            type="search"
            placeholder={searchablePlaceholder || "Rechercher..."}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      )}
      <div className="sui-multi-checkbox-facet">
        {filteredItems.length === 0 && (
          <div style={{ fontSize: "14px", color: "#666" }}>
            {searchQuery ? "Aucun résultat pour \u00ab " + searchQuery + " \u00bb" : ""}
          </div>
        )}
        {filteredItems.map((item) => (
          <label key={item.value} className="sui-multi-checkbox-facet__option-label">
            <div className="sui-multi-checkbox-facet__option-input-wrapper">
              <input
                type="checkbox"
                className="sui-multi-checkbox-facet__checkbox"
                checked={item.isRefined}
                onChange={() => refine(item.value)}
              />
              <span className="sui-multi-checkbox-facet__input-text">{resolveLabel(item.label, item.count)}</span>
            </div>
            <span className="sui-multi-checkbox-facet__option-count">{item.count.toLocaleString("fr-CH")}</span>
          </label>
        ))}
      </div>
      {canToggleShowMore && (
        <button type="button" className="sui-facet-view-more" onClick={toggleShowMore} style={{ marginTop: "8px", background: "none", border: "none", color: "#3a56e4", cursor: "pointer", fontWeight: "bold" }}>
          {isShowingMore ? "- Voir moins" : "+ Voir plus"}
        </button>
      )}
    </div>
  );
}

// Version accordéon pliable, masqué si aucun résultat.
// La facette n'est chargée (hook useRefinementList) qu'au premier dépliage.
function AccordionFacetContent({ attribute, title, searchable, searchablePlaceholder, limit = 10, showMoreLimit = 50, labelMap, extractLabel, onOpenModal }) {
  const { items, refine, canToggleShowMore, isShowingMore, toggleShowMore } = useRefinementList({ attribute, limit, showMoreLimit });

  if (!items || items.length === 0) return null;

  const resolveLabel = (label) => {
    if (extractLabel) return extractLabel(label);
    if (labelMap && labelMap[label]) return labelMap[label];
    return label;
  };

  return (
    <AccordionDetails>
      <div className="sui-multi-checkbox-facet">
        {items.length === 0 && (
          <div style={{ fontSize: "0.875rem", color: "#666" }}>Aucun résultat trouvé</div>
        )}
        {items.map((item) => (
          <label key={item.value} className="sui-multi-checkbox-facet__option-label">
            <div className="sui-multi-checkbox-facet__option-input-wrapper">
              <input
                type="checkbox"
                className="sui-multi-checkbox-facet__checkbox"
                checked={item.isRefined}
                onChange={() => refine(item.value)}
              />
              <span className="sui-multi-checkbox-facet__input-text">{resolveLabel(item.label)}</span>
            </div>
            <span className="sui-multi-checkbox-facet__option-count">{item.count.toLocaleString("fr-CH")}</span>
          </label>
        ))}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "8px" }}>
        <div>
          {canToggleShowMore && (
            <button type="button" className="sui-facet-view-more" onClick={toggleShowMore} style={{ background: "none", border: "none", color: "#3a56e4", cursor: "pointer", fontWeight: "bold" }}>
              {isShowingMore ? "- Voir moins" : "+ Voir plus"}
            </button>
          )}
        </div>
        <div style={{ flexGrow: 1, textAlign: "right" }}>
          {items.length >= limit && (
            <button type="button" onClick={onOpenModal} style={{ background: "none", border: "none", color: "inherit", cursor: "pointer", fontSize: "0.75rem", textDecoration: "underline" }}>
              Tout afficher
            </button>
          )}
        </div>
      </div>
    </AccordionDetails>
  );
}

export function OptionalAccordionFacet({ attribute, title, searchable, searchablePlaceholder, limit = 10, showMoreLimit = 50, labelMap, extractLabel }) {
  const [mounted, setMounted] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMounted, setModalMounted] = useState(false);

  // Vérifier si la facette a des données (limit=1 → très léger)
  const { items: checkItems } = useRefinementList({ attribute, limit: 1 });

  // Masquer la facette si aucun résultat
  if (!checkItems || checkItems.length === 0) return null;

  return (
    <>
      <Accordion onChange={(_e, expanded) => { if (expanded) setMounted(true); }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <legend className="sui-facet__title">{title}</legend>
        </AccordionSummary>
        {mounted && (
          <AccordionFacetContent
            attribute={attribute}
            title={title}
            searchable={searchable}
            searchablePlaceholder={searchablePlaceholder}
            limit={limit}
            showMoreLimit={showMoreLimit}
            labelMap={labelMap}
            extractLabel={extractLabel}
            onOpenModal={() => { setModalMounted(true); setModalOpen(true); }}
          />
        )}
      </Accordion>
      {modalMounted && (
        <FacetModal
          attribute={attribute}
          title={title}
          searchable={searchable}
          searchablePlaceholder={searchablePlaceholder}
          extractLabel={extractLabel}
          labelMap={labelMap}
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          modalLimit={showMoreLimit}
        />
      )}
    </>
  );
}
